import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType, PointerEvent as ReactPointerEvent } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./ThresholdPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Threshold" — an ONBOARDING paywall built on CONTRAST FRAMING. The whole
// screen is one interactive wall of the user's real memories, cut in two by a
// draggable BEAM OF LIGHT. To the right of the beam the photos sit as they do
// today — loose in the camera roll, cool and exposed, tagged "Visible in
// Photos", "In your backups". To the left of the beam the *same* photos are
// inside the vault: warm, glowing, locked, "Only you". You physically drag the
// beam across your library to move every memory to the private side of the
// line — and the CTA finishes the sweep you started.
//
// Nothing is reused from another paywall. The mechanic (a swept threshold that
// re-states each photo from exposed to protected), the warm/cool split palette,
// and the live "protected %" are all new. The emotional register is relief and
// belonging, not alarm — the honest, App-Store-safe truth that your photos are
// simply somewhere only you can open. Fonts + colour family are the house set.
//
// Yearly is the hero: the largest price on screen, per-month broken out, a
// savings badge, the default selection, and the trial timeline built on it.
// Monthly is offered smaller and secondary. Copy never implies photos are lost
// on cancel — the vault is a private place to keep them, not a hostage.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const mem = (n: number) => `${PUB}/threshold/mem${n}.jpg`

// The memory wall. Same markup drives both layers (exposed + protected) so the
// beam wipes styling, never geometry — each photo re-states cleanly across the
// seam. `a` is the tile's aspect ratio, driving a calm masonry in CSS columns.
type Tile = { n: number; a: number }
const WALL: Tile[] = [
  { n: 7, a: 0.82 },
  { n: 1, a: 1.18 },
  { n: 11, a: 0.9 },
  { n: 4, a: 1.05 },
  { n: 6, a: 0.78 },
  { n: 3, a: 1.16 },
  { n: 9, a: 0.94 },
  { n: 2, a: 1.1 },
  { n: 12, a: 0.86 },
  { n: 5, a: 1.2 },
  { n: 8, a: 0.92 },
  { n: 10, a: 1.04 },
]

// Floating truth-tags. Exposed tags sit to the right of the beam's rest point,
// vault tags to the left — so the sweep reveals the change in state.
const EXPOSED_TAGS = [
  { top: "16%", right: "7%", label: "Visible in Photos" },
  { top: "63%", right: "11%", label: "In your backups" },
]
const VAULT_TAGS = [
  { top: "13%", left: "7%", label: "Locked" },
  { top: "44%", left: "5%", label: "Only you" },
  { top: "74%", left: "9%", label: "Encrypted" },
]

type Feature = {
  key: string
  Icon: ComponentType<{ className?: string }>
  color: string
  title: string
  desc: string
}

const FEATURES: Feature[] = [
  {
    key: "lock",
    Icon: svg.featureIcons.FacedownLock,
    color: "#c9873f",
    title: "Sealed behind your passcode & Face ID",
    desc: "Photos and videos leave the camera roll and open only for you — no thumbnail, no preview, no peeking.",
  },
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    color: "#3f7fc9",
    title: "Encrypted cloud backup",
    desc: "A private second copy, locked with a key only you hold. Lose the phone and every memory is still waiting.",
  },
  {
    key: "organize",
    Icon: svg.featureIcons.OrganizationPlus,
    color: "#5aa06a",
    title: "Albums that keep themselves tidy",
    desc: "Bring your library in and it sorts into clean, private albums — the whole story, in order, just for you.",
  },
  {
    key: "alerts",
    Icon: svg.featureIcons.Breakin,
    color: "#b5566d",
    title: "Break-in alerts",
    desc: "If someone tries the wrong passcode, the vault quietly snaps their photo so you know who reached for it.",
  },
]

const START = 34 // beam rest point (% protected) after the invite sweep

function ThresholdPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [reveal, setReveal] = useState(0) // % of library protected
  const [dragging, setDragging] = useState(false)
  const [sealed, setSealed] = useState(false)
  const [launching, setLaunching] = useState(false)
  const [showClose, setShowClose] = useState(false)

  const canvasRef = useRef<HTMLDivElement>(null)
  const touchedRef = useRef(false) // has the user taken over the beam?
  const rafRef = useRef<number | undefined>(undefined)
  const closeTimer = useRef<number | undefined>(undefined)

  // Invite sweep: on load the beam glides from 0 → START, teaching the gesture
  // and showing the first memories cross into the vault. It yields the instant
  // the user grabs the beam, and is skipped entirely under reduced motion.
  useEffect(() => {
    closeTimer.current = window.setTimeout(() => setShowClose(true), 1400)

    if (reduce) {
      setReveal(START)
      return () => window.clearTimeout(closeTimer.current)
    }

    const begin = performance.now()
    const DELAY = 420
    const DUR = 1500
    const tick = (now: number) => {
      if (touchedRef.current) return
      const t = Math.min(1, Math.max(0, (now - begin - DELAY) / DUR))
      const eased = 1 - Math.pow(1 - t, 3)
      setReveal(eased * START)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      window.clearTimeout(closeTimer.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [reduce])

  const setFromClientX = useCallback((clientX: number) => {
    const el = canvasRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const pct = ((clientX - rect.left) / rect.width) * 100
    const clamped = Math.max(0, Math.min(100, pct))
    setReveal(clamped)
    if (clamped >= 99) setSealed(true)
  }, [])

  const takeOver = () => {
    touchedRef.current = true
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
  }

  const onPointerDown = (e: ReactPointerEvent) => {
    takeOver()
    setDragging(true)
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    setFromClientX(e.clientX)
  }
  const onPointerMove = (e: ReactPointerEvent) => {
    if (!dragging) return
    setFromClientX(e.clientX)
  }
  const onPointerUp = () => setDragging(false)

  const handleClose = () => native.postNativeEvent("skip")

  const handleSubscribe = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    setLaunching(true)
    // The CTA finishes the sweep the user started: the beam races to 100%,
    // the wall seals, then the native purchase fires.
    if (!reduce) {
      const from = reveal
      const begin = performance.now()
      const DUR = 560
      const run = (now: number) => {
        const t = Math.min(1, (now - begin) / DUR)
        const eased = 1 - Math.pow(1 - t, 3)
        setReveal(from + (100 - from) * eased)
        if (t < 1) requestAnimationFrame(run)
        else setSealed(true)
      }
      requestAnimationFrame(run)
    } else {
      setReveal(100)
      setSealed(true)
    }
    window.setTimeout(
      () => {
        if (p) native.subscribe(p)
      },
      reduce ? 0 : 720,
    )
  }

  const pct = Math.round(reveal)
  const ctaLabel =
    plan === "yearly"
      ? "Protect my whole library"
      : `Protect for ${monthlyPrice}/mo`
  const ctaSub =
    plan === "yearly"
      ? `${trialDays} days free, then ${yearlyPrice}/year · just ${yearlyPerMonth}/mo`
      : `Billed ${monthlyPrice} every month · cancel anytime`

  const renderWall = (variant: "exposed" | "vault") => (
    <div className={styles.grid}>
      {WALL.map((t) => (
        <span
          className={styles.tile}
          key={t.n}
          style={{ aspectRatio: String(t.a) }}
        >
          <img src={mem(t.n)} alt="" draggable={false} />
          {variant === "vault" && (
            <span className={styles.tileLock} aria-hidden>
              <svg viewBox="0 0 24 24" width="11" height="11">
                <path
                  d="M8 10V8a4 4 0 0 1 8 0v2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="6"
                  y="10"
                  width="12"
                  height="9"
                  rx="2.2"
                  fill="currentColor"
                />
              </svg>
            </span>
          )}
        </span>
      ))}
    </div>
  )

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        {/* ── The interactive threshold ─────────────────────────────────── */}
        <div className={styles.hero}>
          <div className={styles.topbar}>
            <span className={styles.brand}>
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
                <rect
                  x="4"
                  y="10"
                  width="16"
                  height="11"
                  rx="2.6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path
                  d="M8 10V7.5a4 4 0 0 1 8 0V10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              Private Photo Vault
            </span>
            <button
              className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
              onClick={handleClose}
              aria-label="Not now"
              tabIndex={showClose ? 0 : -1}
            >
              Not now
            </button>
          </div>

          <div
            className={`${styles.canvas} ${sealed ? styles.canvasSealed : ""}`}
            ref={canvasRef}
            style={{ ["--reveal" as any]: `${reveal}%` }}
          >
            {/* exposed base layer — the camera roll as it is today */}
            <div className={styles.layerExposed} aria-hidden>
              {renderWall("exposed")}
              <span className={styles.exposedWash} />
              {EXPOSED_TAGS.map((tg) => (
                <span
                  className={styles.tagExposed}
                  key={tg.label}
                  style={{ top: tg.top, right: tg.right }}
                >
                  <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden>
                    <path
                      d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <circle cx="12" cy="12" r="2.4" fill="currentColor" />
                  </svg>
                  {tg.label}
                </span>
              ))}
            </div>

            {/* protected layer — the same photos, inside the vault. Clipped to
                the left of the beam so the sweep reveals it. */}
            <div className={styles.layerVault} aria-hidden>
              {renderWall("vault")}
              <span className={styles.vaultWash} />
              {VAULT_TAGS.map((tg) => (
                <span
                  className={styles.tagVault}
                  key={tg.label}
                  style={{ top: tg.top, left: (tg as any).left }}
                >
                  <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden>
                    <path
                      d="M5 12.5l4.4 4.4L19 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {tg.label}
                </span>
              ))}
            </div>

            {/* live readout of how much of the library is protected */}
            <div className={styles.meter}>
              <span className={styles.meterPct}>{pct}%</span>
              <span className={styles.meterLabel}>in your vault</span>
            </div>

            {/* the beam + draggable lock handle */}
            <div
              className={styles.beam}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              role="slider"
              aria-label="Drag to move your memories into the vault"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <span className={styles.beamLine} />
              <span
                className={`${styles.handle} ${
                  dragging ? styles.handleGrab : ""
                }`}
              >
                <span className={styles.handleGlow} aria-hidden />
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
                  <rect
                    x="4.5"
                    y="10.5"
                    width="15"
                    height="10.5"
                    rx="2.6"
                    fill="currentColor"
                  />
                  <path
                    d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className={styles.handleArrows} aria-hidden>
                  <svg viewBox="0 0 24 24" width="26" height="12">
                    <path
                      d="M9 6l-4 6 4 6M15 6l4 6-4 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </span>
            </div>

            {/* first-run hint, fades once the user takes over */}
            <span
              className={`${styles.hint} ${
                touchedRef.current || pct > 55 ? styles.hintGone : ""
              }`}
              aria-hidden
            >
              drag to protect
            </span>
          </div>
        </div>

        {/* ── Headline ─────────────────────────────────────────────────── */}
        <header className={styles.copy}>
          <p className={styles.eyebrow}>Set up your vault</p>
          <h1 className={styles.title}>
            The same memories.
            <br />
            <span className={styles.titleWarm}>
              One side anyone can see. One side is only yours.
            </span>
          </h1>
          <p className={styles.sub}>
            Everything in your camera roll can be opened by any app, any backup,
            anyone holding your phone. Sweep it across the line and it lives
            inside an encrypted vault that opens for you alone.
          </p>
        </header>

        {/* ── What the protected side gives you ────────────────────────── */}
        <ul className={styles.features}>
          {FEATURES.map((f, i) => (
            <li
              className={styles.feature}
              key={f.key}
              style={{
                ["--c" as any]: f.color,
                ["--d" as any]: `${140 + i * 80}ms`,
              }}
            >
              <span className={styles.featIcon}>
                <f.Icon className={styles.featGlyph} />
              </span>
              <span className={styles.featText}>
                <span className={styles.featTitle}>{f.title}</span>
                <span className={styles.featDesc}>{f.desc}</span>
              </span>
            </li>
          ))}
        </ul>

        {/* ── Trust ────────────────────────────────────────────────────── */}
        <div className={styles.trust}>
          <div className={styles.stars} aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l2.7 5.9 6.4.7-4.8 4.3 1.3 6.3L12 17l-5.6 2.8 1.3-6.3-4.8-4.3 6.4-.7z"
                  fill="currentColor"
                />
              </svg>
            ))}
          </div>
          <p>
            <strong>4.8</strong> · trusted with 6M+ private libraries ·{" "}
            <span className={styles.enc}>256-bit encryption</span>
          </p>
        </div>

        {/* ── Pricing — yearly is the hero ─────────────────────────────── */}
        <section className={styles.pricing} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.planGlow} aria-hidden />
            {savings > 0 && (
              <span className={styles.bestTag}>
                Best value · save {savings}%
              </span>
            )}
            <span className={styles.planTop}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
            </span>
            <span className={styles.priceRow}>
              <span className={styles.priceBig}>{yearlyPrice}</span>
              <span className={styles.pricePer}>/year</span>
            </span>
            <span className={styles.priceFoot}>
              just <b>{yearlyPerMonth}/mo</b> · {trialDays} days free
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planTop}>
              <span className={styles.planName}>Monthly</span>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
            </span>
            <span className={styles.priceRowSm}>
              <span className={styles.priceSm}>{monthlyPrice}</span>
              <span className={styles.pricePerSm}>/month</span>
            </span>
          </button>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <div className={styles.ctaWrap}>
          <button
            className={`${styles.cta} ${launching ? styles.ctaLaunch : ""}`}
            onClick={handleSubscribe}
          >
            <span className={styles.ctaSheen} aria-hidden />
            <span className={styles.ctaText}>{ctaLabel}</span>
            <svg
              className={styles.ctaArrow}
              viewBox="0 0 24 24"
              width="19"
              height="19"
              aria-hidden
            >
              <path
                d="M5 12h13M13 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos stay on your phone until you move them in, and they stay
            yours if you cancel — the vault is simply a private place to keep
            them. Cancel anytime.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThresholdPaywall
